/**
 * CompanyDataForm — Editable form for all CompanyData fields.
 *
 * 3-column grid in compact mode, single column in stacked mode.
 * Parent owns the CompanyData state; this component is fully controlled.
 */

import type { CompanyData } from '@/lib/company-lookup.service'

/** Returns a CompanyData object with all fields set to null/defaults. */
export function emptyCompanyData(): CompanyData {
  return {
    siren: null,
    siret_siege: null,
    legal_name: null,
    commercial_name: null,
    legal_form: null,
    legal_form_code: null,
    naf_code: null,
    naf_label: null,
    address_street: null,
    address_postal_code: null,
    address_city: null,
    address_country: 'FR',
    capital_social: null,
    capital_currency: 'EUR',
    tva_number: null,
    employee_range: null,
    creation_date: null,
  }
}

interface CompanyDataFormProps {
  value: CompanyData
  onChange: (data: CompanyData) => void
  disabled?: boolean
  /** Accent color: "amber" | "violet" */
  accent?: string
  /** "compact" = 3-column grid, "stacked" = single column */
  layout?: 'compact' | 'stacked'
}

// Field definition for rendering
interface FieldDef {
  key: keyof CompanyData
  label: string
  type: 'text' | 'number'
  placeholder: string
  /** Column span in compact grid (out of 3). Default 1. */
  span?: 1 | 2 | 3
}

/** Ordered for logical grouping in a 3-column grid:
 * Row 1: Legal Name (2) + Legal Form (1)
 * Row 2: Commercial Name (1) + Legal Form Code (1) + NAF Code (1)
 * Row 3: Activity (2) + SIREN (1)
 * Row 4: SIRET (1) + TVA Number (1) + Employees (1)
 * Row 5: Street (3)
 * Row 6: Postal Code (1) + City (1) + Country (1)
 * Row 7: Share Capital (1) + Currency (1) + Creation Date (1)
 */
const ALL_FIELDS: FieldDef[] = [
  { key: 'legal_name', label: 'Legal Name', type: 'text', placeholder: 'Registered company name', span: 2 },
  { key: 'legal_form', label: 'Legal Form', type: 'text', placeholder: 'e.g. SAS, SARL, SA' },
  { key: 'commercial_name', label: 'Commercial Name', type: 'text', placeholder: 'Trading name (if different)' },
  { key: 'legal_form_code', label: 'Legal Form Code', type: 'text', placeholder: 'e.g. 5710' },
  { key: 'naf_code', label: 'NAF Code', type: 'text', placeholder: 'e.g. 62.01Z' },
  { key: 'naf_label', label: 'Activity', type: 'text', placeholder: 'e.g. Computer programming', span: 2 },
  { key: 'siren', label: 'SIREN', type: 'text', placeholder: '9-digit SIREN' },
  { key: 'siret_siege', label: 'SIRET (HQ)', type: 'text', placeholder: '14-digit SIRET' },
  { key: 'tva_number', label: 'VAT Number', type: 'text', placeholder: 'e.g. FR12345678901' },
  { key: 'employee_range', label: 'Employees', type: 'text', placeholder: 'e.g. 10-49' },
  { key: 'address_street', label: 'Street', type: 'text', placeholder: 'Street address', span: 3 },
  { key: 'address_postal_code', label: 'Postal Code', type: 'text', placeholder: 'e.g. 75008' },
  { key: 'address_city', label: 'City', type: 'text', placeholder: 'e.g. Paris' },
  { key: 'address_country', label: 'Country', type: 'text', placeholder: 'e.g. FR' },
  { key: 'capital_social', label: 'Share Capital', type: 'number', placeholder: 'e.g. 10000' },
  { key: 'capital_currency', label: 'Currency', type: 'text', placeholder: 'EUR' },
  { key: 'creation_date', label: 'Creation Date', type: 'text', placeholder: 'e.g. 2020-01-15' },
]

const SPAN_CLASS: Record<number, string> = {
  1: '',
  2: 'sm:col-span-2',
  3: 'sm:col-span-3',
}

export default function CompanyDataForm({
  value,
  onChange,
  disabled = false,
  accent = 'amber',
  layout = 'stacked',
}: CompanyDataFormProps) {
  const accentRing = `focus:ring-${accent}-500/50 focus:border-${accent}-500`

  const handleFieldChange = (key: keyof CompanyData, raw: string) => {
    let parsed: string | number | null
    if (raw === '') {
      parsed = null
    } else if (key === 'capital_social') {
      const num = Number(raw)
      parsed = isNaN(num) ? null : num
    } else {
      parsed = raw
    }
    onChange({ ...value, [key]: parsed })
  }

  const fieldValue = (key: keyof CompanyData): string => {
    const v = value[key]
    if (v == null) return ''
    return String(v)
  }

  const gridClass = layout === 'compact'
    ? 'grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-3'
    : 'space-y-3'

  const renderField = (field: FieldDef) => {
    const span = field.span ?? 1
    const spanCls = layout === 'compact' ? SPAN_CLASS[span] : ''
    return (
      <div key={field.key} className={spanCls || undefined}>
        <label className="block text-[11px] font-semibold text-slate-500 dark:text-slate-400 mb-1">
          {field.label}
        </label>
        <input
          type={field.type}
          value={fieldValue(field.key)}
          onChange={(e) => handleFieldChange(field.key, e.target.value)}
          placeholder={field.placeholder}
          disabled={disabled}
          className={`w-full px-3 py-2 bg-white/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-400 focus:outline-none focus:ring-2 ${accentRing} transition-all disabled:opacity-60`}
        />
      </div>
    )
  }

  return (
    <div className={gridClass}>
      {ALL_FIELDS.map(renderField)}
    </div>
  )
}
